import {
  ArrowLeft,
  Camera,
  Lightbulb,
  Sparkles,
  Wand2,
  Wrench,
  Bot,
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { BentoGrid, BentoTile } from '@/shared/ui/bento'
import { Reveal } from '@/shared/motion/reveal'
import { Section, SectionHeader } from '@/shared/ui/section'
import { defineT, useTranslation } from '@/shared/i18n/use-translation'
import type { IconComponent } from '@/shared/types/icon'
import { cn } from '@/shared/lib/cn'
import { asset } from '@/shared/lib/asset'
import { fetchFreeToolsCategories } from '@/features/free-tools/api'
import { SEED_TOOLS } from '@/features/tools-hub/data/tools'
import { ZATOONA_TOTAL } from '@/features/zatoona/data/categories'

const T = defineT({
  eyebrow: { ar: 'مكتبتنا الكاملة', en: 'The full library' },
  title: {
    ar: 'كنز إبداعي بين إيديك',
    en: 'A creative treasure at your fingertips',
  },
  description: {
    ar: 'أدوات، برومبتات، شروحات، ومكتبات بصرية مجانية ومدفوعة. تركيز على الجودة، التنظيم، والاستخدام السريع.',
    en: 'Free and paid tools, prompts, tutorials, and visual libraries — organized for fast retrieval and obsessive quality.',
  },
  // CTA per tile
  explore: { ar: 'استكشف', en: 'Explore' },
  // Tools Hub
  toolshubTitle: { ar: 'دليل الأدوات', en: 'Tools Hub' },
  toolshubDesc: {
    ar: 'دليل احترافي لأدوات الـ AI — مرتب بالفئة، السعر، حالة الاستخدام.',
    en: 'A curated directory of AI tools — by category, price, and use case.',
  },
  // Free Tools
  freetoolsTitle: { ar: 'أدوات مجانية', en: 'Free Tools' },
  freetoolsDesc: {
    ar: 'أدوات بنبنيها وبنطلقها مجاناً — توفّر وقتك من غير اشتراك.',
    en: 'Tools we build and ship free. Save time without a subscription.',
  },
  // Zatoona
  zatoonaTitle: { ar: 'الزتونة', en: 'Zatoona' },
  zatoonaDesc: {
    ar: 'مكتبة بصرية متكاملة: زوايا الكاميرا، الإضاءة، ستايلات الصور، التدريج اللوني، حركة الكاميرا، تعابير الوجه.',
    en: 'A visual encyclopedia: camera angles, lighting, image styles, color grading, camera motion, expressions.',
  },
  zatoonaCount: { ar: '+{n} مرجع بصري', en: '{n}+ visual references' },
  promptsCount: { ar: '+{n} برومبت', en: '{n}+ prompts' },
  toolshubCount: { ar: '+{n} أداة AI', en: '{n}+ AI tools' },
  freetoolsCount: { ar: '+{n} أداة', en: '{n}+ tools' },
  // Prompt Library
  promptsTitle: { ar: 'مكتبة البرومبتات', en: 'Prompt Library' },
  promptsDesc: {
    ar: 'مئات البرومبتات المختبرة، مع توضيحات وأمثلة صور.',
    en: 'Hundreds of tested prompts with examples and outputs.',
  },
  // Camera angles
  anglesTitle: { ar: 'زوايا الكاميرا', en: 'Camera Angles' },
  anglesDesc: {
    ar: 'كل زاوية واستخدامها مع برومبت جاهز.',
    en: 'Every angle, its use, and a ready-to-copy prompt.',
  },
  // Lighting
  lightingTitle: { ar: 'الإضاءة', en: 'Lighting Styles' },
  lightingDesc: {
    ar: 'أساليب الإضاءة الأساسية والمتقدمة.',
    en: 'Core and advanced lighting setups.',
  },
  // Vibe Coding
  vibeTitle: { ar: 'Vibe Coding', en: 'Vibe Coding' },
  vibeDesc: {
    ar: 'مكتبة Skills و Agents و MCPs لتسريع شغلك مع Claude Code.',
    en: 'A library of Skills, Agents, and MCPs to supercharge Claude Code.',
  },
  vibeCount: { ar: '+50 مكوّن', en: '50+ components' },
  // Design Systems
  dsTitle: { ar: 'أنظمة التصميم', en: 'Design Systems' },
  dsDesc: {
    ar: '71 نظام DESIGN.md جاهز لـ Cursor و Claude Code — ألوان وخطوط من Airbnb و Apple.',
    en: '71 DESIGN.md systems for Cursor & Claude Code — colours & typography from Airbnb, Apple & more.',
  },
  // Free APIs
  apisTitle: { ar: 'APIs مجانية', en: 'Free APIs' },
  apisDesc: {
    ar: 'دليل APIs مجانية للمطور العربي — صلاة، طقس، رياضة، AI.',
    en: 'Free APIs directory for Arab developers — prayer, weather, sports, AI.',
  },
})

interface Tile {
  key: string
  to: string
  icon: IconComponent
  titleKey: keyof typeof T
  descKey: keyof typeof T
  countKey?: keyof typeof T
  /** Resolves the count number from the queried data. */
  count?: number
  bg: string
  col: 1 | 2 | 3
  row: 1 | 2
  featured?: boolean
  image?: string
}

const TILES: Tile[] = [
  {
    key: 'zatoona',
    to: '/zatoona',
    icon: Camera,
    titleKey: 'zatoonaTitle',
    descKey: 'zatoonaDesc',
    countKey: 'zatoonaCount',
    bg: 'zatoona-camera-angles.webp',
    col: 3,
    row: 2,
    featured: true,
    image: '/zatoona-camera-angles.webp',
  },
  {
    key: 'toolshub',
    to: '/toolshub',
    icon: Wrench,
    titleKey: 'toolshubTitle',
    descKey: 'toolshubDesc',
    countKey: 'toolshubCount',
    bg: '',
    col: 3,
    row: 1,
    image: '/hero-toolshub.webp',
  },
  {
    key: 'prompts',
    to: '/prompts',
    icon: Wand2,
    titleKey: 'promptsTitle',
    descKey: 'promptsDesc',
    countKey: 'promptsCount',
    bg: '',
    col: 3,
    row: 1,
    image: '/hero-prompts.webp',
  },
  {
    key: 'freetools',
    to: '/freetools',
    icon: Sparkles,
    titleKey: 'freetoolsTitle',
    descKey: 'freetoolsDesc',
    countKey: 'freetoolsCount',
    bg: '',
    col: 2,
    row: 1,
    image: '/hero-mytools.webp',
  },
  {
    key: 'angles',
    to: '/zatoona/camera-angles',
    icon: Camera,
    titleKey: 'anglesTitle',
    descKey: 'anglesDesc',
    bg: '',
    col: 2,
    row: 1,
    image: '/zatoona-camera-movements.webp',
  },
  {
    key: 'lighting',
    to: '/zatoona/lighting',
    icon: Lightbulb,
    titleKey: 'lightingTitle',
    descKey: 'lightingDesc',
    bg: '',
    col: 2,
    row: 1,
    image: '/zatoona-lighting.webp',
  },
  {
    key: 'vibe',
    to: '/vibe-coding',
    icon: Bot,
    titleKey: 'vibeTitle',
    descKey: 'vibeDesc',
    countKey: 'vibeCount',
    bg: '',
    col: 2,
    row: 1,
    image: '/zatoona-color-grading.webp',
  },
  {
    key: 'design-systems',
    to: '/design-systems',
    icon: Wrench,
    titleKey: 'dsTitle',
    descKey: 'dsDesc',
    bg: '',
    col: 2,
    row: 1,
    image: '/card-engineering.webp',
  },
  {
    key: 'free-apis',
    to: '/free-apis',
    icon: Bot,
    titleKey: 'apisTitle',
    descKey: 'apisDesc',
    bg: '',
    col: 2,
    row: 1,
    image: '/card-building.webp',
  },
]

export function ResourcesShowcase() {
  const { t, isRtl } = useTranslation(T)

  // NOTE: we deliberately do NOT fetch the full prompts list here.
  // This section only needs an approximate count for a "+N" badge that
  // rounds down to buckets (50/100/200/…), so pulling all ~270 prompt
  // rows (~148KB JSON) on the home page was pure waste. We use a known
  // floor instead; the real catalog count lives on /prompts.
  const PROMPTS_APPROX = 270

  const freeToolsQuery = useQuery({
    queryKey: ['free-tools', 'categories'],
    queryFn: ({ signal }) => fetchFreeToolsCategories(signal),
    staleTime: 30 * 60 * 1000,
  })

  const counts: Record<string, number> = {
    zatoona: ZATOONA_TOTAL,
    prompts: PROMPTS_APPROX,
    toolshub: SEED_TOOLS.length,
    freetools:
      freeToolsQuery.data?.reduce(
        (sum, c) => sum + c.tools.filter((tool) => tool.isActive).length,
        0,
      ) ?? 0,
  }

  // Round counts down to a friendly bucket so the badge reads as "100+"
  // not "103". Buckets: 50, 100, 200, 300, 500, 1000.
  const bucket = (n: number) => {
    const buckets = [10, 25, 50, 100, 200, 300, 500, 1000]
    let last = 0
    for (const b of buckets) {
      if (n >= b) last = b
    }
    return last
  }

  return (
    <Section id="resources" pad="md">
      <Reveal>
        <SectionHeader
          eyebrow={t('eyebrow')}
          title={t('title')}
          description={t('description')}
        />
      </Reveal>

      <div className="stagger">
        <BentoGrid>
          {TILES.map((tile) => {
            const realCount = counts[tile.key]
            const countLabel =
              tile.countKey && realCount && realCount > 0
                ? t(tile.countKey).replace(
                    '{n}',
                    String(bucket(realCount) || realCount),
                  )
                : null
            return (
              <Reveal key={tile.key} className="contents">
                <BentoTile
                  href={tile.to}
                  col={tile.col}
                  row={tile.row}
                  featured={tile.featured}
                  className={cn(
                    'group flex flex-col',
                    tile.image && '!p-0',
                  )}
                >
                  {tile.image ? (
                    /* Full-bleed image card — image fills the tile,
                     * gradient overlay keeps the text readable.
                     * Featured tile gets extra height. */
                    <div
                      className={cn(
                        'relative flex h-full flex-col justify-end p-6 sm:p-7',
                        tile.featured ? 'min-h-[24rem] sm:p-8' : 'min-h-[14rem]',
                      )}
                    >
                      <img
                        src={asset(tile.image)}
                        alt=""
                        className={cn(
                          'absolute inset-0 h-full w-full object-cover transition-all duration-700',
                          tile.featured
                            ? 'opacity-40 group-hover:scale-[1.04] group-hover:opacity-50'
                            : 'opacity-55 group-hover:opacity-70',
                        )}
                        loading="lazy"
                        decoding="async"
                        aria-hidden
                      />
                      <div
                        className="absolute inset-0 bg-gradient-to-t from-card via-card/85 to-card/30"
                        aria-hidden
                      />
                      <div className="relative">
                        <span
                          className="inline-grid h-12 w-12 place-items-center rounded-xl bg-primary/15 text-primary ring-1 ring-primary/30 backdrop-blur-md"
                          aria-hidden
                        >
                          <tile.icon className="h-6 w-6" />
                        </span>
                        <div className="mt-5 flex flex-wrap items-center gap-2">
                          <h3
                            className={cn(
                              'font-display font-bold tracking-tight',
                              tile.featured
                                ? 'text-2xl sm:text-3xl'
                                : 'text-xl',
                            )}
                          >
                            {t(tile.titleKey)}
                          </h3>
                          {countLabel && (
                            <span
                              className={cn(
                                'inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-medium tabular-nums',
                                tile.featured
                                  ? 'border border-primary/30 bg-primary/10 px-3 py-1 text-xs text-primary'
                                  : 'border border-border bg-secondary/70 text-muted-foreground',
                              )}
                            >
                              {countLabel}
                            </span>
                          )}
                        </div>
                        <p
                          className={cn(
                            'mt-2 text-sm leading-relaxed text-muted-foreground',
                            tile.featured && 'max-w-prose',
                          )}
                        >
                          {t(tile.descKey)}
                        </p>
                        <ExploreCta isRtl={isRtl} label={t('explore')} />
                      </div>
                    </div>
                  ) : (
                    /* No-image tile — flat card. */
                    <div className="flex h-full flex-col">
                      <span
                        className="grid h-12 w-12 place-items-center rounded-xl bg-primary/10 text-primary ring-1 ring-primary/20"
                        aria-hidden
                      >
                        <tile.icon className="h-6 w-6" />
                      </span>
                      <div className="mt-5 flex flex-wrap items-center gap-2">
                        <h3 className="font-display text-xl font-bold tracking-tight">
                          {t(tile.titleKey)}
                        </h3>
                        {countLabel && (
                          <span className="inline-flex rounded-full border border-border bg-secondary/50 px-2.5 py-0.5 text-[11px] font-medium text-muted-foreground tabular-nums">
                            {countLabel}
                          </span>
                        )}
                      </div>
                      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                        {t(tile.descKey)}
                      </p>
                      <ExploreCta isRtl={isRtl} label={t('explore')} />
                    </div>
                  )}
                </BentoTile>
              </Reveal>
            )
          })}
        </BentoGrid>
      </div>
    </Section>
  )
}

function ExploreCta({ isRtl, label }: { isRtl: boolean; label: string }) {
  return (
    <span className="mt-auto inline-flex items-center gap-2 pt-6 text-sm font-medium text-primary">
      {label}
      <ArrowLeft
        className={cn(
          'h-4 w-4 transition-transform duration-300',
          isRtl
            ? 'group-hover:-translate-x-1'
            : 'rotate-180 group-hover:translate-x-1',
        )}
        aria-hidden
      />
    </span>
  )
}
